import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import { scrollToSection } from "@/lib/scroll";

const LINKS = [
  { label: "About", target: "#about" },
  { label: "Products", target: "#products" },
  { label: "Services", target: "#services" },
  { label: "Contact", target: "#contact" },
];

export default function Navbar({ loaded }) {
  const [open, setOpen] = useState(false);

  const go = (target) => {
    setOpen(false);
    scrollToSection(target);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={loaded ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      data-testid="main-navigation"
      className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/60 bg-white/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="nav-logo-home"
          onClick={() => go("#top")}
          className="cursor-pointer"
          aria-label="JISLL home"
        >
          <LogoLockup />
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.target)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600 transition-colors hover:text-ink"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-brand transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-5 md:flex">
          <span
            data-testid="nav-status-badge"
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-600"
          >
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-emerald-500" />
            Systems Normal
          </span>
          <button
            data-testid="nav-cta-request-consultation"
            onClick={() => go("#contact")}
            className="group flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-brand hover:shadow-[0_8px_24px_rgba(0,82,255,0.35)]"
          >
            Request Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-ink md:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200/60 bg-white/95 px-5 pb-6 pt-3 backdrop-blur-xl md:hidden">
          {LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l.target)}
              className="block w-full border-b border-slate-100 py-4 text-left font-heading text-lg font-semibold text-ink"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-cta"
            onClick={() => go("#contact")}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-3.5 text-sm font-medium text-white"
          >
            Request Consultation
            <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </motion.header>
  );
}
